/**
 * StripeFunnelPanel — Admin panel that visualises the Stripe-checkout
 * drop-off funnel across every funnel (7days, nabd, ipo, course).
 *
 * Stages:
 *   1. Sessions created    → row in `*_orders` with status="pending"
 *   2. Payment attempted   → user actually entered card details on Stripe
 *                            (= status=paid OR a fail_code was recorded)
 *   3. Payment failed      → card declined / 3DS abandoned
 *   4. Payment succeeded   → webhook fulfilment confirmed
 *
 * The MASSIVE gap between (1) and (2) is what caused the user's "40 attempts
 * not showing on Stripe" mystery — they never reached step 2.
 *
 * Data source: GET /api/admin/stripe-funnel — pure MongoDB aggregation, no
 * live Stripe calls per request. Run the per-product Reconcile button first
 * to sync the latest decline codes from Stripe into local rows.
 */

import { useEffect, useState } from "react";
import {
  TrendingDown, RefreshCw, AlertTriangle, ShoppingBag, CreditCard,
  XCircle, CheckCircle2, Activity, Loader2, Send, Check,
} from "lucide-react";
import { adminApi } from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("ar-EG").format(n);
const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * ResendableRow — one row of the recent_failures table. Renders the visitor's
 * data + a "Resend link" button that emails them a fresh inline-checkout URL.
 *
 * Button states:
 *   1. Idle           "Resend"            (clickable if row has email or admin types one)
 *   2. Needs email    "+ Email"           (inline prompt if row.email is empty)
 *   3. Sending        spinner             (during POST)
 *   4. Already sent   "Sent ✓"            (if row.recovery_email_sent_at is set)
 *   5. Error          red toast inline
 */
const ResendableRow = ({ row, pkey }) => {
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(row.recovery_email_sent_at || null);
  const [sentTo, setSentTo] = useState(row.recovery_email_to || row.email || null);
  const [err, setErr] = useState(null);
  const [needEmail, setNeedEmail] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState("");

  const token = row.order_token || row.public_token || row.order_id || "";
  const hasEmail = Boolean(row.email);

  const send = async (email = "") => {
    if (sending) return;
    setSending(true);
    setErr(null);
    try {
      const c = await adminApi();
      const { data } = await c.post("/admin/funnel/resend-link", {
        product: pkey,
        order_token: token,
        ...(email ? { override_email: email } : {}),
      });
      setSentAt(data.sent_at);
      setSentTo(data.sent_to);
      setNeedEmail(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const onClick = () => {
    if (sentAt) return; // already sent
    if (hasEmail) return send();
    setNeedEmail(true);
  };

  const onSubmitOverride = (e) => {
    e.preventDefault();
    const v = (overrideEmail || "").trim().toLowerCase();
    if (!v || !v.includes("@")) { setErr("Enter a valid email"); return; }
    send(v);
  };

  return (
    <tr className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`resendable-row-${token.slice(0, 12)}`}>
      <td className="px-3 py-2 text-white tabular-nums whitespace-nowrap">{fmtDate(row.created_at)}</td>
      <td className="px-3 py-2 text-white">{row.country || "—"}</td>
      <td className="px-3 py-2 text-white max-w-[180px] truncate" title={row.email || "(anonymous)"}>
        {row.email || <span className="text-[#A1B0C6] italic">(anonymous)</span>}
      </td>
      <td className="px-3 py-2">
        <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{
          background: row.fail_decline_code ? "rgba(248,113,113,0.18)" : "rgba(161,176,198,0.12)",
          color: row.fail_decline_code ? "#FCA5A5" : "#A1B0C6",
        }}>
          {row.fail_decline_code ? "DECLINED" : (row.stripe_status || row.status || "pending").toUpperCase()}
        </span>
      </td>
      <td className="px-3 py-2 text-white max-w-[260px] truncate" title={row.fail_message || row.reason}>
        {row.fail_decline_code || row.reason || "—"}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {sentAt ? (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-1 rounded"
            style={{ background: "rgba(52,211,153,0.16)", color: "#6EE7B7" }}
            title={`Sent to ${sentTo} at ${fmtDate(sentAt)}`}
            data-testid={`resend-sent-${token.slice(0, 12)}`}
          >
            <Check className="w-3 h-3" /> Sent
          </span>
        ) : needEmail ? (
          <form onSubmit={onSubmitOverride} className="flex items-center gap-1.5">
            <input
              type="email"
              autoFocus
              required
              placeholder="email@example.com"
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              className="bg-[#0A1428] border border-[#C5A059]/30 text-white text-[11px] px-2 py-1 rounded-sm w-44 focus:outline-none focus:border-[#C5A059]/70"
              data-testid={`resend-email-input-${token.slice(0, 12)}`}
            />
            <button
              type="submit"
              disabled={sending}
              className="bg-[#C5A059] hover:bg-[#D8B47A] text-[#0A1428] font-bold text-[10.5px] px-2.5 py-1 rounded-sm disabled:opacity-60"
              data-testid={`resend-submit-${token.slice(0, 12)}`}
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send"}
            </button>
          </form>
        ) : (
          <button
            onClick={onClick}
            disabled={sending}
            className="inline-flex items-center gap-1 bg-[#0A1428] hover:bg-[#0F1E3B] border border-[#C5A059]/30 hover:border-[#C5A059]/60 text-white text-[10.5px] font-bold px-2.5 py-1 rounded-sm transition-colors disabled:opacity-60"
            data-testid={`resend-btn-${token.slice(0, 12)}`}
            title={hasEmail ? `Email ${row.email}` : "Type an email — this session has no email on record"}
          >
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {hasEmail ? "Resend" : "+ Email"}
          </button>
        )}
        {err && <div className="text-[10px] text-[#FCA5A5] mt-1 max-w-[180px]" data-testid={`resend-err-${token.slice(0, 12)}`}>{err}</div>}
      </td>
    </tr>
  );
};

const PRODUCT_LABELS = {
  reply: { label: "Reply Rescue", emoji: "💬", color: "#E8A18F" },
  seven: { label: "7 ليالي بيننا", emoji: "💑", color: "#C9A66A" },
  nabd: { label: "نبض السوق AI", emoji: "📈", color: "#F4B842" },
  ipo: { label: "IPO Access", emoji: "🏛️", color: "#3B82F6" },
  course: { label: "Performance Ads OS", emoji: "🎯", color: "#A78BFA" },
};

const FunnelStage = ({ icon: Icon, label, count, total, color, testid }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex-1 min-w-[140px]" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.8} />
        <span className="text-[11px] tracking-[0.15em] text-[#A1B0C6] uppercase">{label}</span>
      </div>
      <div className="font-display text-3xl font-black text-white tabular-nums" dir="ltr">{fmt(count)}</div>
      <div className="text-[11px] text-[#A1B0C6] mt-1 tabular-nums" dir="ltr">{fmtPct(pct)} of created</div>
      <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, pct)}%`, background: color }}
        />
      </div>
    </div>
  );
};

const UA_LABEL = {
  tiktok_iab: { label: "TikTok IAB", color: "#FE2C55", isIab: true },
  instagram_iab: { label: "Instagram IAB", color: "#E1306C", isIab: true },
  facebook_iab: { label: "Facebook IAB", color: "#1877F2", isIab: true },
  snapchat_iab: { label: "Snapchat IAB", color: "#FFFC00", isIab: true },
  linkedin_iab: { label: "LinkedIn IAB", color: "#0A66C2", isIab: true },
  twitter_iab: { label: "Twitter IAB", color: "#1DA1F2", isIab: true },
  safari: { label: "Safari (real)", color: "#34D399", isIab: false },
  chrome: { label: "Chrome (real)", color: "#34D399", isIab: false },
  firefox: { label: "Firefox (real)", color: "#34D399", isIab: false },
  other_browser: { label: "Other browser", color: "#7DD3FC", isIab: false },
  other: { label: "Unknown UA", color: "#A1B0C6", isIab: false },
};

const UABreakdown = ({ uaStats, pkey }) => {
  if (!uaStats || (!uaStats.total_classified && !uaStats.no_ua_legacy)) return null;
  const buckets = Object.entries(uaStats.buckets || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="mb-5 bg-[#0A1428]/60 rounded-sm p-4 border border-white/[0.04]" dir="ltr" data-testid={`stripe-funnel-${pkey}-ua-breakdown`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] tracking-[0.15em] text-[#A1B0C6] uppercase">Traffic Source (User Agent)</div>
        {uaStats.iab_total > 0 && (
          <div className={`text-[11.5px] font-bold ${uaStats.iab_pct >= 50 ? "text-[#FCA5A5]" : "text-[#FBBF24]"}`}>
            {uaStats.iab_pct}% in-app browser
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {buckets.map(([k, count]) => {
          const meta = UA_LABEL[k] || { label: k, color: "#A1B0C6", isIab: false };
          const pct = uaStats.total_classified > 0 ? (count / uaStats.total_classified) * 100 : 0;
          return (
            <div key={k} className="flex items-center gap-3 text-[12px]">
              <div className="w-32 truncate flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: meta.color }}></span>
                <span className={meta.isIab ? "text-[#FCA5A5] font-bold" : "text-white"}>{meta.label}</span>
              </div>
              <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: meta.color, opacity: meta.isIab ? 0.9 : 0.7 }}></div>
              </div>
              <div className="w-24 text-right text-white tabular-nums" dir="ltr">
                <span className="font-bold">{count}</span>
                <span className="text-[#A1B0C6] ml-1.5">({pct.toFixed(0)}%)</span>
              </div>
            </div>
          );
        })}
      </div>
      {uaStats.no_ua_legacy > 0 && (
        <div className="text-[11px] text-[#A1B0C6] mt-3 pt-3 border-t border-white/[0.05]" dir="ltr">
          {uaStats.no_ua_legacy} legacy rows without user-agent data (created before this feature shipped — ignore for analysis).
        </div>
      )}
      {uaStats.iab_total > 0 && uaStats.iab_pct >= 50 && (
        <div className="text-[11.5px] text-[#FCA5A5] mt-3 pt-3 border-t border-[#F87171]/30 leading-relaxed" dir="ltr">
          <span className="font-bold">⚠️ More than half of unpaid traffic is from in-app browsers.</span> This is likely WHY conversion is low — Stripe Payment Element breaks silently inside TikTok/Meta IABs. Mitigation: the IAB Blocker is deployed; consider pivoting ad CTAs to a WhatsApp link.
        </div>
      )}
    </div>
  );
};

const ProductFunnel = ({ pkey, data, uaStats }) => {
  const meta = PRODUCT_LABELS[pkey] || { label: pkey, emoji: "📦", color: "#7DD3FC" };
  const {
    sessions_created, payment_attempted, payment_failed, payment_succeeded,
    attempt_rate_pct, success_rate_pct, decline_breakdown = [],
    recent_failures = [], checkout_mode_breakdown = {},
  } = data;
  // Bulk recovery state — counts how many unpaid rows can still be emailed
  // (= unpaid + has email + no recovery sent yet) and triggers the bulk send.
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkErr, setBulkErr] = useState(null);

  const sendAll = async () => {
    if (bulkLoading) return;
    const confirmMsg = `Send recovery emails to ALL unpaid ${meta.label} sessions that have an email? This may take a minute (rate-limited 1/sec).`;
    if (!window.confirm(confirmMsg)) return;
    setBulkLoading(true);
    setBulkErr(null);
    try {
      const c = await adminApi();
      const { data: r } = await c.post("/admin/funnel/resend-all", { product: pkey });
      setBulkResult(r);
    } catch (e) {
      setBulkErr(e?.response?.data?.detail || "Bulk send failed");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div
      className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6"
      data-testid={`stripe-funnel-${pkey}`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="text-lg">{meta.emoji}</span>
          <h3 className="font-display text-lg font-bold text-white">{meta.label}</h3>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={sendAll}
            disabled={bulkLoading || sessions_created === 0}
            className="inline-flex items-center gap-1.5 bg-[#0A1428] hover:bg-[#0F1E3B] border border-[#C5A059]/30 hover:border-[#C5A059]/60 text-white text-[11px] font-bold px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
            data-testid={`stripe-funnel-${pkey}-bulk-resend`}
            title="Send recovery emails to every unpaid session that has an email captured"
          >
            {bulkLoading
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>
              : <><Send className="w-3 h-3" /> Send recovery to all</>}
          </button>
          <div className="text-[11px] text-[#A1B0C6] flex items-center gap-3 tabular-nums" dir="ltr">
            <span>attempt: <span className="text-white font-bold">{fmtPct(attempt_rate_pct)}</span></span>
            <span>·</span>
            <span>success: <span className="text-white font-bold">{fmtPct(success_rate_pct)}</span></span>
          </div>
        </div>
      </div>
      {/* Bulk-send result toast — shows the scanned/sent/skipped totals
          after a "Send recovery to all" click. Auto-clears on next load. */}
      {bulkResult && (
        <div className="mb-4 rounded-sm bg-[rgba(52,211,153,0.10)] border border-[rgba(52,211,153,0.35)] px-3 py-2 text-[12px] text-[#A7F3D0] flex items-center justify-between" data-testid={`stripe-funnel-${pkey}-bulk-result`} dir="ltr">
          <div>
            ✅ Scanned <span className="font-bold text-white">{bulkResult.scanned}</span> ·
            sent <span className="font-bold text-white">{bulkResult.sent}</span> ·
            skipped <span className="font-bold text-white">{bulkResult.skipped_invalid_email + bulkResult.skipped_send_fail}</span>
          </div>
          <button onClick={() => setBulkResult(null)} className="text-[#A7F3D0]/70 hover:text-white" aria-label="dismiss">✕</button>
        </div>
      )}
      {bulkErr && (
        <div className="mb-4 rounded-sm bg-[rgba(248,113,113,0.10)] border border-[rgba(248,113,113,0.35)] px-3 py-2 text-[12px] text-[#FCA5A5]" data-testid={`stripe-funnel-${pkey}-bulk-err`} dir="ltr">
          {bulkErr}
        </div>
      )}

      {/* Funnel stages */}
      <div className="flex flex-wrap gap-5 mb-6">
        <FunnelStage
          icon={ShoppingBag} label="Sessions created" count={sessions_created}
          total={sessions_created} color={meta.color}
          testid={`stripe-funnel-${pkey}-stage-created`}
        />
        <FunnelStage
          icon={CreditCard} label="Card entered" count={payment_attempted}
          total={sessions_created} color="#7DD3FC"
          testid={`stripe-funnel-${pkey}-stage-attempted`}
        />
        <FunnelStage
          icon={XCircle} label="Failed" count={payment_failed}
          total={sessions_created} color="#F87171"
          testid={`stripe-funnel-${pkey}-stage-failed`}
        />
        <FunnelStage
          icon={CheckCircle2} label="Paid" count={payment_succeeded}
          total={sessions_created} color="#34D399"
          testid={`stripe-funnel-${pkey}-stage-paid`}
        />
      </div>

      {/* Critical drop-off insight */}
      {sessions_created > 0 && payment_attempted === 0 && (
        <div
          className="rounded-lg p-3 mb-5 flex items-start gap-2 text-[12.5px]"
          style={{ background: "rgba(248,113,113,0.08)", borderColor: "rgba(248,113,113,0.30)", border: "1px solid rgba(248,113,113,0.30)" }}
          data-testid={`stripe-funnel-${pkey}-no-attempts-alert`}
        >
          <AlertTriangle className="w-4 h-4 text-[#F87171] shrink-0 mt-0.5" />
          <div className="text-[#FECACA]" dir="ltr">
            <span className="font-bold">{fmt(sessions_created)} sessions opened, ZERO card attempts.</span>{" "}
            Visitors landed on the inline checkout but never typed card details. Common causes: billing-country dropdown defaulting to the wrong country, mobile keyboard issues, or trust friction. Check the form yourself on the live site to confirm it loads cleanly.
          </div>
        </div>
      )}

      {/* Inline vs hosted breakdown — shows lift from the Inline migration */}
      {(checkout_mode_breakdown.inline > 0 || checkout_mode_breakdown.hosted > 0) && (
        <div className="flex items-center gap-4 mb-5 text-[12px] text-[#A1B0C6]" dir="ltr">
          <span>checkout mode:</span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#34D399]"></span>
            inline <span className="font-bold text-white tabular-nums">{fmt(checkout_mode_breakdown.inline || 0)}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#A1B0C6]"></span>
            hosted (legacy) <span className="font-bold text-white tabular-nums">{fmt(checkout_mode_breakdown.hosted || 0)}</span>
          </span>
        </div>
      )}

      {/* NEW — Traffic-source breakdown (TikTok/IG/FB IAB vs real browsers) */}
      <UABreakdown uaStats={uaStats} pkey={pkey} />

      {/* Decline-code breakdown */}
      {decline_breakdown.length > 0 && (
        <div className="mb-5" data-testid={`stripe-funnel-${pkey}-decline-breakdown`}>
          <div className="text-[11px] tracking-[0.15em] text-[#A1B0C6] uppercase mb-2">Top decline reasons</div>
          <div className="space-y-1.5">
            {decline_breakdown.map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-[12.5px] bg-[#0A1428] rounded-sm px-3 py-2 border border-white/[0.04]">
                <div className="flex-1 min-w-0">
                  <span className="text-white font-bold">{d.label}</span>
                  <span className="text-[#A1B0C6] ml-2 text-[11px]" dir="ltr">({d.code})</span>
                </div>
                <span className="text-white font-bold tabular-nums" dir="ltr">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent failures table */}
      {recent_failures.length > 0 && (
        <div data-testid={`stripe-funnel-${pkey}-recent-failures`}>
          <div className="text-[11px] tracking-[0.15em] text-[#A1B0C6] uppercase mb-2">Recent unpaid sessions ({recent_failures.length})</div>
          <div className="border border-white/[0.06] rounded-sm overflow-hidden" dir="ltr">
            <table className="w-full text-[11.5px]">
              <thead className="bg-[#0A1428] text-[#A1B0C6]">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Created</th>
                  <th className="text-left px-3 py-2 font-normal">Country</th>
                  <th className="text-left px-3 py-2 font-normal">Email</th>
                  <th className="text-left px-3 py-2 font-normal">Status</th>
                  <th className="text-left px-3 py-2 font-normal">Decline / Reason</th>
                  <th className="text-left px-3 py-2 font-normal">Recovery</th>
                </tr>
              </thead>
              <tbody>
                {recent_failures.map((r, i) => (
                  <ResendableRow key={i} row={r} pkey={pkey} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sessions_created === 0 && (
        <div className="text-center text-[12px] text-[#A1B0C6] py-6">لا توجد بيانات بعد</div>
      )}
    </div>
  );
};

export const StripeFunnelPanel = () => {
  const [data, setData] = useState(null);
  const [uaStats, setUaStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const c = await adminApi();
      const [funnel, ua] = await Promise.all([
        c.get("/admin/stripe-funnel"),
        c.get("/admin/funnel/ua-stats").catch(() => ({ data: null })),
      ]);
      setData(funnel.data);
      setUaStats(ua.data);
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="max-w-7xl mx-auto py-10 text-center text-[#A1B0C6]" data-testid="stripe-funnel-loading">
        <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
        تحميل بيانات القمع…
      </div>
    );
  }

  if (err) {
    return (
      <div className="max-w-7xl mx-auto py-6" data-testid="stripe-funnel-error">
        <div className="bg-[rgba(229,72,77,0.10)] border border-[rgba(229,72,77,0.30)] rounded-sm p-4 text-[#FECACA] text-sm">
          {err}
        </div>
      </div>
    );
  }

  const { summary = {}, by_product = {} } = data || {};

  return (
    <div className="max-w-7xl mx-auto space-y-6" dir="ltr" data-testid="stripe-funnel-panel">
      {/* Header + refresh */}
      <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.22em] text-[#C5A059] font-bold mb-1 flex items-center gap-2">
            <TrendingDown className="w-3.5 h-3.5" /> STRIPE CHECKOUT FUNNEL
          </div>
          <p className="text-[12.5px] text-[#A1B0C6] leading-relaxed max-w-3xl">
            See where visitors drop off between &quot;opened checkout&quot; and &quot;card charged&quot;.
            With inline checkout deployed, the gap between <span className="text-white font-bold">Sessions Created</span> and{" "}
            <span className="text-white font-bold">Card Entered</span> represents visitors who saw the payment form but never typed card details — typically a UX friction issue (wrong country pre-selected, mobile layout glitch, low trust) rather than a Stripe problem.
          </p>
          {refreshedAt && (
            <div className="text-[10.5px] text-[#A1B0C6]/70 mt-2 tabular-nums">
              Last updated {fmtDate(refreshedAt.toISOString())}
            </div>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="stripe-funnel-refresh"
          className="inline-flex items-center gap-2 bg-[#0A1428] border border-[#C5A059]/30 hover:border-[#C5A059]/60 text-white text-[12.5px] px-4 py-2 rounded-sm transition-colors disabled:opacity-60"
        >
          {loading
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Refreshing…</>
            : <><RefreshCw className="w-3.5 h-3.5" /> Refresh</>}
        </button>
      </div>

      {/* Cross-product summary */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="stripe-funnel-summary">
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-5">
          <div className="text-[10.5px] tracking-[0.22em] text-[#A1B0C6] uppercase mb-1">Total Sessions</div>
          <div className="font-display text-3xl font-black text-white tabular-nums">{fmt(summary.sessions_created || 0)}</div>
        </div>
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-5">
          <div className="text-[10.5px] tracking-[0.22em] text-[#A1B0C6] uppercase mb-1">Cards Entered</div>
          <div className="font-display text-3xl font-black text-white tabular-nums">{fmt(summary.payment_attempted || 0)}</div>
          <div className="text-[10.5px] text-[#A1B0C6] mt-1 tabular-nums">{fmtPct(summary.attempt_rate_pct)} attempt rate</div>
        </div>
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-5">
          <div className="text-[10.5px] tracking-[0.22em] text-[#A1B0C6] uppercase mb-1">Paid</div>
          <div className="font-display text-3xl font-black text-white tabular-nums">{fmt(summary.payment_succeeded || 0)}</div>
          <div className="text-[10.5px] text-[#A1B0C6] mt-1 tabular-nums">{fmtPct(summary.success_rate_pct)} success rate</div>
        </div>
      </div>

      {/* Per-product funnels */}
      <div className="space-y-4">
        {Object.entries(by_product).map(([k, v]) => (
          <ProductFunnel key={k} pkey={k} data={v} uaStats={uaStats?.[k]} />
        ))}
      </div>
    </div>
  );
};
